import React from 'react';
import { useDispatch } from 'react-redux';

import { Grid, Tab, TabProps } from 'semantic-ui-react';
import { useFormState } from 'react-final-form';
import flatten from 'flat';
import { SprayEventOperatorsIndex } from 'services/sprayEvents/types';
import { setSprayEventMultipleOperatorsTabIndex } from 'services/sprayEventMultipleOperatorsTab/actions';
import { TabName } from './constants';
import './styles.scss';

interface Props {
    lastSubmittedAt?: Date;
    childForOutsideRowTab: React.ReactNode;
    childForDoubleRowTab: React.ReactNode;
    tabActiveIndex?: number;
    needSetActiveIndexToState?: boolean;
}

interface Pane {
    menuItem: TabName;
    pane: React.ReactElement;
}

export default function MultipleOperatorsTab(props: Props): React.ReactElement {
    const {
        lastSubmittedAt,
        childForOutsideRowTab,
        childForDoubleRowTab,
        tabActiveIndex = 0,
        needSetActiveIndexToState = false
    } = props;

    const dispatch = useDispatch();

    const tabElement = React.useRef<any>(null);
    const formState = useFormState();

    const handleOnTabChange = (data: TabProps) => {
        if (needSetActiveIndexToState) {
            dispatch(
                setSprayEventMultipleOperatorsTabIndex(
                    data.activeIndex as number
                )
            );
        }
    };

    React.useEffect(() => {
        if (tabElement.current) {
            tabElement.current.handleItemClick(null, {
                index: tabActiveIndex
            });
        }
    }, [tabActiveIndex]);

    React.useEffect(() => {
        const { submitFailed, errors } = formState;

        if (submitFailed && errors && tabElement.current) {
            const { activeIndex } = tabElement.current.state;
            const { panes } = tabElement.current.props;

            const tabNameIndexes = panes.reduce(
                (result: Record<TabName, number>, pane: Pane, idx: number) => {
                    const newResult = { ...result };

                    newResult[pane.menuItem] = idx;

                    return newResult;
                },
                {}
            );

            const minErrorIdx = Object.keys(flatten(errors)).reduce(
                (minIdx: number | undefined, fieldName: string) => {
                    let idx;

                    if (
                        fieldName.indexOf(
                            SprayEventOperatorsIndex.OUTSIDE.toString()
                        ) >= 0
                    ) {
                        idx = tabNameIndexes[TabName.OutsideRow];
                    } else if (
                        fieldName.indexOf(
                            SprayEventOperatorsIndex.DOUBLE.toString()
                        ) >= 0
                    ) {
                        idx = tabNameIndexes[TabName.DoubleRow];
                    }

                    if (idx === undefined) return minIdx;
                    if (minIdx === undefined) return idx;

                    return idx < minIdx ? idx : minIdx;
                },
                undefined
            );

            if (minErrorIdx !== undefined && minErrorIdx !== activeIndex) {
                tabElement.current.handleItemClick(null, {
                    index: minErrorIdx
                });
            }
        }
    }, [lastSubmittedAt]);

    const getPanes = (): Pane[] => {
        const allPanes: Pane[] = [
            {
                menuItem: TabName.OutsideRow,
                pane: (
                    <Tab.Pane
                        className="spray-event-form-tab-pane"
                        attached={false}
                        key={TabName.OutsideRow}
                    >
                        <Grid>
                            <Grid.Row>
                                <Grid.Column>
                                    {childForOutsideRowTab}
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Tab.Pane>
                )
            },
            {
                menuItem: TabName.DoubleRow,
                pane: (
                    <Tab.Pane
                        className="spray-event-form-tab-pane"
                        attached={false}
                        key={TabName.DoubleRow}
                    >
                        <Grid>
                            <Grid.Row>
                                <Grid.Column>
                                    {childForDoubleRowTab}
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Tab.Pane>
                )
            }
        ];

        return allPanes;
    };

    return (
        <Grid.Row>
            <Grid.Column>
                <Tab
                    ref={tabElement}
                    panes={getPanes()}
                    menu={{
                        secondary: true,
                        pointing: true,
                        widths: getPanes().length
                    }}
                    renderActiveOnly={false}
                    className="spray-event-form-tab"
                    onTabChange={(event, data) => handleOnTabChange(data)}
                />
            </Grid.Column>
        </Grid.Row>
    );
}
